import { ApplicationConfig, importProvidersFrom, provideAppInitializer, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideClientHydration, BrowserModule } from '@angular/platform-browser';

import { ClipboardService } from 'ngx-clipboard';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, APP_ID } from '@angular/core';
import { DateAge, DateAgeBadge } from './helpers/dateAge.pipe';
import { RackLocationPipe } from './helpers/rackLocation.pipe';
import { ConfigService } from './configHelper';
import { AuthInterceptor } from './data-services/authintercept.provider';
import { SecurityService } from './security/security.service';
import { ErrorLogger } from './logging/ErrorLogger';
import { PendingChangesGuard } from './helpers/pending-changes.guard';
import { } from '@progress/kendo-angular-common';
import { DialogModule } from 'primeng/dialog';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Material from '@primeuix/themes/material';
import Nora from '@primeuix/themes/nora';

// Required for MSAL
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { async, firstValueFrom } from 'rxjs';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(configService: ConfigService): IPublicClientApplication {
  console.log("web MSAL Instance");
  let entraAuthority: string = `https://login.microsoftonline.com/${configService.getConfig().entraTenantId}`;
  let use20Authority: string = configService.getConfig().entraUse20Authority;

  if (use20Authority) {
    entraAuthority = `https://login.microsoftonline.com/${configService.getConfig().entraTenantId}/v2.0`;
  }

  return new PublicClientApplication({
    auth: {
      // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
      clientId: configService.getConfig().entraClientId,
      // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
      authority: entraAuthority,
      // Must be the same redirectUri as what was provided in your app registration.
      redirectUri: `${configService.getConfig().entraRedirectURL}auth/callback`,
      postLogoutRedirectUri: `${configService.getConfig().entraRedirectURL}logout/callback`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    }
  });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(configService: ConfigService): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      state: window.location.host.toLowerCase()
    }
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(configService: ConfigService): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
      state: window.location.host.toLowerCase()
    }
  };
}

const provideConfiguration = provideAppInitializer(() => {
  const appConfig = inject(ConfigService);
  console.log("initializing configuration");
  return appConfig.initializeAppFactory()();
});


export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideRouter(routes),
    //importProvidersFrom(BrowserModule, FormsModule),
    //provideClientHydration(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      }
    }),
    DialogModule,
    { provide: APP_ID, useValue: 'lt-angular-app' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorLogger },
    provideConfiguration,
    importProvidersFrom(PendingChangesGuard),
    DatePipe,
    importProvidersFrom(DateAge),
    importProvidersFrom(DateAgeBadge),
    RackLocationPipe,
    importProvidersFrom(ConfigService),
    importProvidersFrom(ClipboardService),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ConfigService]
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
}
